import axios from "axios";
import { getApiV1Url } from './config/api';
import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from 'react-router-dom';
import "./styles/App.css";
import "./styles/CertificatesPage.css"
import DeclarationsTable from "./components/Declarations/DeclarationsTable";
import DeclarationsDynamics from "./components/Declarations/DeclarationsDynamics";
import DeclarationsFilter from "./components/Filters/DeclarationsFilter";
import UniversalPagination from "./components/UniversalPagination";
import { getPdfButtonLabel, useRegistryPdfExport } from "./hooks/useRegistryPdfExport";
import {
    buildDeclarationRegistryCard,
    buildDeclarationsSummary,
    describeDeclarationFilters,
} from "./utils/declarationsPdfUtils";
import React from 'react';
import { ResponsivePie } from '@nivo/pie';
import { Declaration, DeclarationBasis, DeclarationFilterField, DeclarationFilters } from "./types/declaration";
import {
    getDeclarationDetailPath,
    getDeclarationStatus,
    getEmptyDeclarationFilters,
    getFilterFieldValues,
    getLabNames,
    getLegalBasis,
    getRegYear,
    getStatusColor,
    getStatusCounts
} from "./utils/declarationUtils";

interface DeclarationsPageProps {
    basis: DeclarationBasis;
    isFilterOpen: boolean;
    setIsFilterOpen: (open: boolean) => void;
}

// Дата в формате YYYY-MM-DD попадает в период (пустая граница периода не ограничивает)
const isDateInRange = (date: string | null, range: { start: string; end: string }) => {
    if (!range.start && !range.end) return true;
    if (!date) return false;
    if (range.start && date < range.start) return false;
    if (range.end && date > range.end) return false;
    return true;
};

function DeclarationsPage({ basis, isFilterOpen, setIsFilterOpen }: DeclarationsPageProps) {
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [declarations, setDeclarations] = useState<Declaration[]>([]);
    const [filters, setFilters] = useState<DeclarationFilters>(getEmptyDeclarationFilters());

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    const { pdfProgress, startPdfExport, pdfReport } = useRegistryPdfExport();

    const [sortField, setSortField] = useState<keyof Declaration | ''>('');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

    const [viewportWidth, setViewportWidth] = useState<number>(typeof window !== 'undefined' ? window.innerWidth : 1920);
    useEffect(() => {
        const onResize = () => setViewportWidth(window.innerWidth);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    useEffect(() => {
        if (isFilterOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        const handleEsc = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isFilterOpen) {
                setIsFilterOpen(false);
            }
        };
        window.addEventListener('keydown', handleEsc);
        return () => {
            document.body.style.overflow = '';
            window.removeEventListener('keydown', handleEsc);
        };
    }, [isFilterOpen, setIsFilterOpen]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const access = localStorage.getItem('access');
                const response = await axios.get(getApiV1Url('DECLARATIONS'), {
                    params: { legal_basis: getLegalBasis(basis) },
                    headers: access ? { Authorization: `Bearer ${access}` } : {},
                    timeout: 30000
                });
                setDeclarations(Array.isArray(response.data) ? response.data : []);
            } catch (err: any) {
                if (err.code === 'ECONNABORTED') {
                    setError('Превышено время ожидания ответа от сервера');
                } else {
                    setError('Ошибка при загрузке данных');
                }
                console.error('Ошибка загрузки деклараций:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [basis]);

    const filteredDeclarations = useMemo(() => {
        const normalizeText = (text: string) => text?.toLowerCase().trim() || '';

        return declarations.filter(declaration => {
            const matchesStatus = filters.statuses.length === 0 ||
                filters.statuses.includes(getDeclarationStatus(declaration));

            const matchesYear = filters.years.length === 0 ||
                filters.years.includes(getRegYear(declaration));

            const matchesDates = isDateInRange(declaration.reg_date, filters.regDate) &&
                isDateInRange(declaration.end_date, filters.endDate);

            const matchesFields = Object.entries(filters.fields).every(([field, values]) => {
                if ((values as string[]).length === 0) return true;
                const declarationValues = getFilterFieldValues(declaration, field as DeclarationFilterField);
                return (values as string[]).some(value =>
                    declarationValues.some(declarationValue =>
                        normalizeText(declarationValue).includes(normalizeText(value))
                    )
                );
            });

            return matchesStatus && matchesYear && matchesDates && matchesFields;
        });
    }, [declarations, filters]);

    const sortedDeclarations = useMemo(() => {
        if (!sortField) return filteredDeclarations;
        return [...filteredDeclarations].sort((first, second) => {
            const firstValue = (first[sortField] || '').toString();
            const secondValue = (second[sortField] || '').toString();
            const result = firstValue.localeCompare(secondValue, 'ru', { numeric: true });
            return sortDirection === 'asc' ? result : -result;
        });
    }, [filteredDeclarations, sortField, sortDirection]);

    useEffect(() => {
        setCurrentPage(1);
    }, [filters, sortField, sortDirection]);

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = sortedDeclarations.slice(indexOfFirstItem, indexOfLastItem);

    const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

    const handleItemsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newItemsPerPage = parseInt(e.target.value);
        setItemsPerPage(newItemsPerPage);
        setCurrentPage(1);
    };

    const handleSort = (field: keyof Declaration) => {
        if (sortField === field) {
            if (sortDirection === 'asc') {
                setSortDirection('desc');
            } else {
                setSortDirection('asc');
                setSortField('');
            }
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const getSortIcon = (field: keyof Declaration) => {
        if (sortField !== field) return '↕';
        return sortDirection === 'asc' ? '↑' : '↓';
    };

    const handleOpenDeclarationDetails = useCallback((declaration: Declaration) => {
        navigate(getDeclarationDetailPath(basis, declaration.reg_number), { state: { declaration } });
    }, [navigate, basis]);

    const handleStatusPieClick = (datum: any) => {
        const status = String(datum.id);
        const newStatuses = filters.statuses.includes(status)
            ? filters.statuses.filter(item => item !== status)
            : [...filters.statuses, status];
        setFilters({ ...filters, statuses: newStatuses });
    };

    const handleYearBarClick = (year: string) => {
        const newYears = filters.years.includes(year)
            ? filters.years.filter(item => item !== year)
            : [...filters.years, year];
        setFilters({ ...filters, years: newYears });
    };

    const statusPieData = getStatusCounts(filteredDeclarations).map((item, index) => ({
        id: item.status,
        label: item.status,
        value: item.count,
        color: getStatusColor(item.status, index),
    }));

    // Заявители и изготовители считаются по ИНН, если его нет - по названию
    const getUniqueApplicants = () => {
        const applicants = new Set<string>();
        filteredDeclarations.forEach(declaration => {
            const key = declaration.applicant_inn || declaration.applicant_full_name;
            if (key) applicants.add(key);
        });
        return Array.from(applicants);
    };

    const getUniqueManufacturers = () => {
        const manufacturers = new Set<string>();
        filteredDeclarations.forEach(declaration => {
            const key = declaration.manufacturer_inn || declaration.manufacturer_full_name;
            if (key) manufacturers.add(key);
        });
        return Array.from(manufacturers);
    };

    const getUniqueLaboratories = () => {
        const labs = new Set<string>();
        filteredDeclarations.forEach(declaration => {
            getLabNames(declaration).forEach(name => labs.add(name));
        });
        return Array.from(labs);
    };

    const pageTitle = `Реестр деклараций ${getLegalBasis(basis)}`;

    const handleDownload = () => startPdfExport(() => ({
        fileName: `declarations_${basis}_report.pdf`,
        summary: buildDeclarationsSummary(sortedDeclarations, getLegalBasis(basis), describeDeclarationFilters(filters)),
        sections: [{ kind: 'cards', title: pageTitle, cards: sortedDeclarations.map(buildDeclarationRegistryCard) }],
    }));

    if (loading) return (
        <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            width: '100vw',
            position: 'fixed',
            top: 0,
            left: 0,
            zIndex: 9999
        }}>
            <img
                src="/loading.png"
                alt="Загрузка..."
                style={{ width: 200, height: 200, animation: 'spin 1.2s linear infinite' }}
            />
            <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
        </div>
    );

    if (error) return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
            <div style={{
                textAlign: 'center',
                padding: '40px',
                backgroundColor: 'white',
                borderRadius: '8px',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                maxWidth: '500px'
            }}>
                <h3 style={{ color: '#e74c3c', marginBottom: '16px' }}>Ошибка загрузки</h3>
                <p style={{ marginBottom: '20px', color: '#666' }}>{error}</p>
                <button
                    onClick={() => window.location.reload()}
                    style={{
                        padding: '10px 20px',
                        backgroundColor: '#0071bc',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px'
                    }}
                >
                    Попробовать снова
                </button>
            </div>
        </div>
    );

    const noResults = filteredDeclarations.length === 0;

    return (
        <div className="certificates-container">

            <div className="navigation_certificates">
                <nav className="breadcrumbs">
                    <span
                        className="breadcrumb-home"
                        style={{ color: 'rgba(0,0,0,0.5)', cursor: 'pointer', fontWeight: 400 }}
                        onClick={() => navigate('/')}
                    >
                        Главная страница
                    </span>
                    <span style={{ margin: '0 8px', color: 'rgba(0,0,0,0.3)' }}>/</span>
                    <span className="breadcrumb-current" style={{ fontWeight: 500, color: '#000' }}>
                        {pageTitle}
                    </span>
                </nav>
                <button className="download-button" onClick={handleDownload} disabled={pdfProgress !== null || noResults}>
                    {getPdfButtonLabel(pdfProgress)}
                </button>
            </div>
            {pdfReport}
            {isFilterOpen && (
                <div className="certificates-filter-modal-overlay">
                    <div className="certificates-filter-modal">
                        <button className="certificates-filter-modal__close" onClick={() => setIsFilterOpen(false)}>
                            ×
                        </button>
                        <DeclarationsFilter
                            declarations={declarations}
                            filters={filters}
                            onFilterChange={setFilters}
                        />
                    </div>
                </div>
            )}

            {noResults ? (
                <div style={{
                    marginTop: 24,
                    background: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: 8,
                    padding: 24,
                    textAlign: 'center',
                    color: '#374151'
                }}>
                    <h3 style={{ margin: 0, marginBottom: 8 }}>Нет результатов</h3>
                    <div>
                        {declarations.length === 0
                            ? `Деклараций ${getLegalBasis(basis)} пока нет.`
                            : 'По выбранным фильтрам не найдено данных. Измените параметры и попробуйте снова.'}
                    </div>
                </div>
            ) : (
            <div className="certificates-content">
                {/* В CertificatesPage.css блоки скрыты (display: none), на этой странице их показываем */}
                <div className="certificates-stats-blocks" style={{ display: 'flex' }}>
                    <div className="certificates-stats-block">
                        <h3>Общее количество деклараций</h3>
                        <h1 className="stats-value">{filteredDeclarations.length}</h1>
                    </div>
                    <div className="certificates-stats-block">
                        <h3>Общее количество заявителей</h3>
                        <h1 className="stats-value">{getUniqueApplicants().length}</h1>
                    </div>
                    <div className="certificates-stats-block">
                        <h3>Общее количество изготовителей</h3>
                        <h1 className="stats-value">{getUniqueManufacturers().length}</h1>
                    </div>
                    <div className="certificates-stats-block">
                        <h3>Общее количество лабораторий</h3>
                        <h1 className="stats-value">{getUniqueLaboratories().length}</h1>
                    </div>
                </div>

                <div className="certificates-charts" style={{ display: 'flex', alignItems: 'stretch', gap: 32 }}>
                    <div className="chart-container">
                        <h3>Статус декларации в реестре деклараций о соответствии ФСА</h3>
                        <div className="pie-chart" style={{ height: 240, width: 450, minWidth: 220, minHeight: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff' }}>
                            <div style={{ width: 450, height: 240 }}>
                                <ResponsivePie
                                    data={statusPieData}
                                    margin={{
                                        top: 20,
                                        right: viewportWidth <= 430 ? 200 : 270,
                                        bottom: 20,
                                        left: 20
                                    }}
                                    innerRadius={0.5}
                                    padAngle={0.7}
                                    cornerRadius={3}
                                    activeOuterRadiusOffset={16}
                                    colors={statusPieData.map(item => item.color)}
                                    borderWidth={1}
                                    borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                                    enableArcLabels={true}
                                    arcLabel={item => String(item.value)}
                                    enableArcLinkLabels={false}
                                    theme={{
                                        labels: {
                                            text: {
                                                fontWeight: 700,
                                                fill: '#ffffff',
                                            },
                                        },
                                    }}
                                    onClick={handleStatusPieClick}
                                    activeId={filters.statuses.length > 0 ? filters.statuses[filters.statuses.length - 1] : undefined}
                                    legends={[{
                                        anchor: 'right',
                                        direction: 'column',
                                        justify: false,
                                        translateX: 130,
                                        translateY: 0,
                                        itemsSpacing: 4,
                                        itemWidth: 90,
                                        itemHeight: 18,
                                        itemTextColor: '#999',
                                        itemDirection: 'left-to-right',
                                        itemOpacity: 1,
                                        symbolSize: 15,
                                        symbolShape: 'circle',
                                        effects: [
                                            {
                                                on: 'hover',
                                                style: {
                                                    itemTextColor: '#000',
                                                },
                                            },
                                        ],
                                    }]}
                                    tooltip={({ datum }) => {
                                        const total = statusPieData.reduce((sum, item) => sum + item.value, 0);
                                        const percent = total > 0 ? ((datum.value / total) * 100).toFixed(1) : '0.0';
                                        return (
                                            <div style={{ padding: 8, background: '#fff', border: '1px solid #ccc', borderRadius: 4 }}>
                                                <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>{datum.label}</div>
                                                <span style={{ color: datum.color, fontWeight: 700, marginRight: 8 }}>■</span>
                                                <b>{datum.value}</b> <span style={{ color: '#888', marginLeft: 8 }}>({percent}%)</span>
                                            </div>
                                        );
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="chart-container__dinamic" style={{ display: 'block', minHeight: 'auto' }}>
                    <p style={{ textAlign: "left", marginLeft: "20px", marginBottom: "0px", fontWeight: "600" }}>
                        Динамика регистрации деклараций {getLegalBasis(basis)} по годам
                    </p>
                    <DeclarationsDynamics
                        declarations={filteredDeclarations}
                        filteredYears={filters.years}
                        onBarClick={handleYearBarClick}
                    />
                </div>

                <div className="certificates-table">
                    <p>{pageTitle}</p>
                    <div className="items-per-page">
                        <label htmlFor="itemsPerPage">Количество на странице:</label>
                        <select id="itemsPerPage" value={itemsPerPage} onChange={handleItemsPerPageChange}>
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                            <option value={200}>200</option>
                            <option value={sortedDeclarations.length}>Все</option>
                        </select>
                    </div>
                    <DeclarationsTable
                        declarations={currentItems}
                        exportDeclarations={sortedDeclarations}
                        exportFileName={`Декларации ${getLegalBasis(basis)}`}
                        onSort={handleSort}
                        getSortIcon={getSortIcon}
                        onRowClick={handleOpenDeclarationDetails}
                    />
                    <UniversalPagination<Declaration>
                        currentPage={currentPage}
                        itemsPerPage={itemsPerPage}
                        paginate={paginate}
                        items={sortedDeclarations}
                    />
                </div>
            </div>
            )}
        </div>
    );
}

export default DeclarationsPage;
